/**
 * @fileoverview A prova invisível de que quem se cadastra é uma pessoa (Cloudflare
 * Turnstile, 03/11/2026). Roda sozinha com o formulário na tela e só aparece nos
 * raros casos em que pede um clique. Nunca segura a pessoa: se o navegador bloquear
 * a prova, ou ela demorar, o cadastro segue sem o token, e o servidor decide pelo
 * limite (services/AuthServices/humanCheck). Sem a chave do site, não carrega nada.
 */

import { useCallback, useEffect, useRef } from "react";
import { useTheme } from "@material-ui/core/styles";

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const WAIT_FOR_TOKEN_MS = 5000;

let scriptLoading = null;

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!scriptLoading) {
    scriptLoading = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        scriptLoading = null;
        script.remove();
        reject(new Error("turnstile-blocked"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptLoading;
};

export const useHumanCheck = (siteKey) => {
  const { palette } = useTheme();
  const containerRef = useRef(null);
  const widgetId = useRef(null);
  const token = useRef(null);
  const unavailable = useRef(false);
  const waiting = useRef(null);
  // Cada ligação da prova é uma rodada: a resposta de uma rodada antiga não vale.
  const round = useRef(0);

  const deliver = useCallback((value) => {
    token.current = value;
    if (waiting.current) waiting.current(value);
  }, []);

  const start = useCallback(() => {
    const started = round.current;
    unavailable.current = false;
    loadTurnstile()
      .then((turnstile) => {
        if (started !== round.current || !containerRef.current || widgetId.current !== null) return;
        widgetId.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: "signup",
          appearance: "interaction-only",
          // O "flexible" pede 300 px: não cabe nos celulares menores.
          size: window.innerWidth < 340 ? "compact" : "flexible",
          theme: palette.type,
          callback: deliver,
          "expired-callback": () => {
            token.current = null;
          },
          "error-callback": () => {
            deliver(null);
            return true;
          },
        });
      })
      .catch(() => {
        if (started !== round.current) return;
        unavailable.current = true;
        deliver(null);
      });
  }, [siteKey, palette.type, deliver]);

  useEffect(() => {
    if (!siteKey) return undefined;
    round.current += 1;
    start();
    return () => {
      round.current += 1;
      token.current = null;
      if (widgetId.current !== null && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey, start]);

  // O token da prova, ou null se ele não vier logo: a pessoa não fica esperando. Se
  // o script não carregou antes, tenta de novo (a rede pode ter voltado): bloqueado,
  // ele falha na hora.
  const takeToken = useCallback((waitMs = WAIT_FOR_TOKEN_MS) => {
    if (!siteKey) return Promise.resolve(null);
    if (token.current) return Promise.resolve(token.current);
    return new Promise((resolve) => {
      const finish = (value) => {
        clearTimeout(timer);
        waiting.current = null;
        resolve(value);
      };
      const timer = setTimeout(() => finish(null), waitMs);
      waiting.current = finish;
      if (unavailable.current) start();
    });
  }, [siteKey, start]);

  // O token só vale uma vez: depois de um envio recusado, a prova roda de novo.
  const reset = useCallback(() => {
    token.current = null;
    if (widgetId.current !== null && window.turnstile) window.turnstile.reset(widgetId.current);
  }, []);

  return { containerRef, takeToken, reset };
};
